import type { ComponentPropsWithRef, ReactNode } from 'react';
import React, { useMemo, useState } from 'react';

import type { Rect } from '@aline/common';
import {
  combineRects,
  isCannedFieldElement,
  isCannedFieldEntry,
  isCrossReferenceElement,
  isCrossReferenceEntry,
  isLinkElement,
  isLinkEntry,
  isNonSignatureFieldElement,
  isNonSignatureFieldEntry,
  Queries,
} from '@aline/common';
import type { VirtualElement } from '@floating-ui/react-dom';
import { Paper } from '@mui/material';
import { equals } from 'ramda';
import { Editor, Range } from 'slate';
import { AlineButton, defaultEditorPopoverPaperStyles, EditorPopover } from 'src/components';
import { SelectionRibbon } from 'src/editor';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import {
  CannedFieldPopoverContent,
  CrossReferencePopoverContent,
  InsertLinkForm,
  LinkPopoverContent,
  useInsertLink,
  VariablePopoverContent,
} from 'src/editor/popovers';
import { ClientTransforms } from 'src/editor/transforms';
import { IS_MOBILE } from 'src/helpers/environment';
import { makeStyles } from 'tss-react/mui';

import { useOpenHeaderOrFooterPageNum } from './HeaderOrFooter';
import { useEditor, useRangeRects, useSelection, useSlateSelector } from '../hooks';

export function SelectionPopover() {
  const selection = useSelection();
  const activeHeaderOrFooterPageNum = useOpenHeaderOrFooterPageNum();
  const isDraggingSelection = useIsDraggingSelection();
  const insertLinkState = useInsertLink();
  const isGridSelection = useSlateSelector(editor => Queries.getIsGridSelection(editor));

  const selectedElementState = useSlateSelector(editor => {
    const nodeEntry = Editor.above(editor, {
      match: node =>
        isLinkElement(node) ||
        isCrossReferenceElement(node) ||
        isNonSignatureFieldElement(node) ||
        isCannedFieldElement(node),
    });
    if (!nodeEntry) return null;
    const cMRange = Editor.range(editor, nodeEntry[1]);
    return { cMRange, nodeEntry };
  }, equals);

  const { cMRange, content, placement } = useMemo((): {
    cMRange: Range | null;
    content: ReactNode | null;
    placement: 'top' | 'bottom' | null;
  } => {
    if (insertLinkState)
      return {
        cMRange: insertLinkState.insertLinkRange,
        content: <InsertLinkForm {...insertLinkState} />,
        placement: 'bottom',
      };

    if (selectedElementState && !IS_MOBILE) {
      const { cMRange, nodeEntry } = selectedElementState;

      if (isLinkEntry(nodeEntry))
        return {
          cMRange,
          content: <LinkPopoverContent linkEntry={nodeEntry} />,
          placement: 'bottom',
        };

      if (isCrossReferenceEntry(nodeEntry))
        return {
          cMRange,
          content: <CrossReferencePopoverContent crossReferenceEntry={nodeEntry} />,
          placement: 'bottom',
        };

      if (isNonSignatureFieldEntry(nodeEntry))
        return {
          cMRange,
          content: <VariablePopoverContent fieldEntry={nodeEntry} />,
          placement: 'bottom',
        };

      if (isCannedFieldEntry(nodeEntry))
        return {
          cMRange,
          content: <CannedFieldPopoverContent cannedFieldEntry={nodeEntry} />,
          placement: 'bottom',
        };
    }

    /**
     * A grid selection's linear range walks the anchor→focus text run, not the highlighted
     * rectangle, so it is not a meaningful ribbon anchor. It instead anchors to the selected cell
     * rectangle (see gridSelectionRects below) and leaves cMRange null.
     */
    if (selection && !isDraggingSelection && Range.isExpanded(selection))
      return {
        cMRange: isGridSelection ? null : selection,
        /**
         * The document is canvas-rendered, so iOS native long-press selection
         * and the system Copy callout can't operate on it. On mobile we
         * replace the desktop ribbon with a single Copy affordance that fires
         * the same copyToClipboard path as Cmd+C.
         */
        content: IS_MOBILE ? <MobileCopyButton /> : <SelectionRibbon />,
        placement: 'top',
      };

    return { cMRange: null, content: null, placement: null };
  }, [insertLinkState, selectedElementState, selection, isDraggingSelection, isGridSelection]);

  const linearRangeRects = useRangeRects(cMRange);

  // A grid selection anchors to its selected cell rectangle rather than to a linear range.
  const gridSelectionRects = useSlateSelector(
    editor =>
      isGridSelection
        ? Queries.getGridSelectionRectsInDocument(editor, { activeHeaderOrFooterPageNum })
        : null,
    equals
  );

  const rangeRects = isGridSelection ? gridSelectionRects : linearRangeRects;

  /**
   * Reference elements for the first and last line range rects, as well as the combined rect. We
   * pass all three of these to Floating UI so that its inline() middleware can choose the best rect
   * to display the popover on.
   */
  const [combinedReference, setCombinedReference] = useState<HTMLDivElement | null>(null);
  const [firstLineReference, setFirstLineReference] = useState<HTMLDivElement | null>(null);
  const [lastLineReference, setLastLineReference] = useState<HTMLDivElement | null>(null);

  const reference: VirtualElement | null = useMemo(() => {
    if (!combinedReference || !firstLineReference || !lastLineReference) return null;

    /**
     * Include rangeRects in the dependency list so that we give Floating UI a fresh reference
     * object whenever they change.
     */
    rangeRects;

    return {
      getBoundingClientRect: () => combinedReference.getBoundingClientRect(),
      getClientRects: () => [
        firstLineReference.getBoundingClientRect(),
        lastLineReference.getBoundingClientRect(),
      ],
    };
  }, [combinedReference, firstLineReference, lastLineReference, rangeRects]);

  if (!rangeRects || !placement) return null;

  const { firstLineRect, lastLineRect } = rangeRects;
  const combinedRect = combineRects(firstLineRect, lastLineRect);

  return (
    <>
      <RectDiv ref={setCombinedReference} rect={combinedRect} />
      <RectDiv ref={setFirstLineReference} rect={firstLineRect} />
      <RectDiv ref={setLastLineReference} rect={lastLineRect} />

      <EditorPopover gap={IS_MOBILE ? 36 : undefined} placement={placement} reference={reference}>
        {content}
      </EditorPopover>
    </>
  );
}

interface RectDivProps extends ComponentPropsWithRef<'div'> {
  rect: Rect;
}

function RectDiv({ rect, style, ...props }: RectDivProps) {
  return (
    <div
      style={{
        height: rect.heightPx,
        left: rect.leftPx,
        position: 'absolute',
        top: rect.topPx,
        width: rect.widthPx,
        ...style,
      }}
      {...props}
    />
  );
}

const useMobileCopyButtonStyles = makeStyles()(theme => ({
  button: {
    fontWeight: 600,
    minWidth: 0,
    padding: theme.spacing(1, 2.5),
  },
  paper: {
    ...defaultEditorPopoverPaperStyles,
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    maxWidth: 'unset',
    minWidth: 'unset',
    /** Prevent the page from scrolling when the user is trying to tap Copy. */
    touchAction: 'none',
  },
}));

function MobileCopyButton(): JSX.Element {
  const editor = useEditor();
  const { classes } = useMobileCopyButtonStyles();
  const [copied, setCopied] = useState(false);

  async function handleCopy(): Promise<void> {
    const success = await ClientTransforms.copyToClipboard(editor);
    if (!success) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Paper className={classes.paper}>
      <AlineButton className={classes.button} color="primary" onClick={handleCopy}>
        {copied ? 'Copied' : 'Copy'}
      </AlineButton>
    </Paper>
  );
}

function useIsDraggingSelection() {
  /**
   * We clear mouseDownClick onMouseUp so a user is "dragging" whenever there is a mouseDownClick
   * registered.
   */
  return Boolean(useEditorTabStateSelector(state => state.click.mouseDownClick));
}
